import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import UserRolesManager from "./UserRolesManager";
import UserActionsPanel from "./UserActionsPanel";
import { Loader2 } from "lucide-react";

interface UserData {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  created_at: string;
  roles: string[];
  subscription_plan?: string;
  subscription_status?: string;
}

interface UserDetails {
  profile: any;
  reviewsCount: number;
  eventsCount: number;
  companiesCount?: number;
}

interface UserDetailModalProps {
  user: UserData | null;
  userDetails: UserDetails | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: () => void;
  initialTab?: 'details' | 'roles' | 'actions' | 'stats';
}

export default function UserDetailModal({
  user,
  userDetails,
  isOpen,
  onClose,
  onUserUpdated,
  initialTab = 'details',
}: UserDetailModalProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'details' | 'roles' | 'actions' | 'stats'>(initialTab);

  if (!user) return null;

  const handleUserUpdate = () => {
    onUserUpdated();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">
            {t("admin.users.detail.title")}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'details' | 'roles' | 'actions' | 'stats')} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details">{t("admin.users.detail.tabs.details")}</TabsTrigger>
            <TabsTrigger value="roles">{t("admin.users.detail.tabs.roles")}</TabsTrigger>
            <TabsTrigger value="actions">{t("admin.users.detail.tabs.actions")}</TabsTrigger>
            <TabsTrigger value="stats">{t("admin.users.detail.tabs.stats")}</TabsTrigger>
          </TabsList>

          {/* Tab 1: Details */}
          <TabsContent value="details" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.fields.firstName")}</label>
                <p className="text-foreground">{user.first_name || 'N/A'}</p>
              </div>
              
              <div>
                <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.fields.lastName")}</label>
                <p className="text-foreground">{user.last_name || 'N/A'}</p>
              </div>

              <div>
                <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.fields.email")}</label>
                <p className="text-foreground">{user.email}</p>
              </div>
              
              <div>
                <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.fields.userId")}</label>
                <p className="text-xs text-foreground font-mono">{user.id}</p>
              </div>

              <div>
                <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.fields.registrationDate")}</label>
                <p className="text-foreground">
                  {format(new Date(user.created_at), "dd/MM/yyyy HH:mm")}
                </p>
              </div>

              <div>
                <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.fields.subscription")}</label>
                <div className="flex gap-2 items-center">
                  {user.subscription_plan ? (
                    <>
                      <Badge variant="default">{user.subscription_plan}</Badge>
                      <Badge variant={user.subscription_status === 'active' ? 'default' : 'secondary'}>
                        {user.subscription_status}
                      </Badge>
                    </>
                  ) : (
                    <p className="text-muted-foreground">{t("admin.users.detail.fields.noPlan")}</p>
                  )}
                </div>
              </div>

              {userDetails?.profile && (
                <>
                  <div>
                    <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.fields.businessName")}</label>
                    <p className="text-foreground">{userDetails.profile.business_name || 'N/A'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.fields.publicProfile")}</label>
                    {userDetails.profile.slug ? (
                      <a 
                        href={`https://inclusia.pro/professional/${userDetails.profile.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {t("admin.users.detail.fields.viewProfile")}
                      </a>
                    ) : (
                      <p className="text-muted-foreground">{t("admin.users.detail.fields.notAvailable")}</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {!userDetails && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">{t("admin.users.detail.loading.details")}</span>
              </div>
            )}
          </TabsContent>

          {/* Tab 2: Roles */}
          <TabsContent value="roles" className="mt-4">
            <UserRolesManager
              user={user}
              onRolesUpdated={handleUserUpdate}
            />
          </TabsContent>

          {/* Tab 3: Actions */}
          <TabsContent value="actions" className="mt-4">
            <UserActionsPanel
              user={user}
              onActionComplete={handleUserUpdate}
            />
          </TabsContent>

          {/* Tab 4: Statistics */}
          <TabsContent value="stats" className="space-y-4 mt-4">
            {userDetails ? (
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 border-[3px] border-dark bg-card">
                  <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.stats.reviews")}</label>
                  <p className="text-3xl font-heading font-bold text-foreground">
                    {userDetails.reviewsCount}
                  </p>
                </div>

                <div className="p-4 border-[3px] border-dark bg-card">
                  <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.stats.eventsCreated")}</label>
                  <p className="text-3xl font-heading font-bold text-foreground">
                    {userDetails.eventsCount}
                  </p>
                </div>

                <div className="p-4 border-[3px] border-dark bg-card">
                  <label className="text-sm font-bold text-muted-foreground">{t("admin.users.detail.stats.companies")}</label>
                  <p className="text-3xl font-heading font-bold text-foreground">
                    {userDetails.companiesCount || 0}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">{t("admin.users.detail.loading.stats")}</span>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t-[3px] border-dark">
          <BrutalButton variant="secondary" onClick={onClose}>
            {t("admin.users.detail.close")}
          </BrutalButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}